/**
 * EduBox style utilities
 * 
 * This file provides easy access to EduBox component styles through
 * class names and utility functions.
 */

/**
 * Get the base container class for the EduBox
 * @param isEmbedded Whether the EduBox is embedded in another component
 * @returns The appropriate class string
 */
export const getEduBoxContainerClass = (isEmbedded: boolean = false): string => {
  return `edubox-container ${isEmbedded ? 'embedded' : ''}`;
};

/**
 * Get the content container class for the EduBox
 * @returns The content class string
 */
export const getEduBoxContentClass = (): string => {
  return 'edubox-content';
};

/**
 * Get the empty state class for the EduBox
 * @returns The empty state class string
 */
export const getEduBoxEmptyClass = (): string => {
  return 'edubox-empty';
};

/**
 * Get the loading state class for the EduBox
 * @returns The loading state class string
 */
export const getEduBoxLoadingClass = (): string => {
  return 'edubox-loading';
};

/**
 * Get the loading spinner class for the EduBox
 * @returns The loading spinner class string
 */
export const getEduBoxLoadingSpinnerClass = (): string => {
  return 'edubox-loading-spinner';
};

/**
 * Get the highlight class for important information
 * @returns The highlight class string
 */
export const getEduBoxHighlightClass = (): string => {
  return 'edubox-highlight';
};

/**
 * Get the callout box class
 * @returns The callout box class string
 */
export const getEduBoxCalloutClass = (): string => {
  return 'edubox-callout';
};

/**
 * Get the callout title class
 * @returns The callout title class string
 */
export const getEduBoxCalloutTitleClass = (): string => {
  return 'edubox-callout-title';
};

/**
 * Get the button class
 * @returns The button class string
 */
export const getEduBoxButtonClass = (): string => {
  return 'edubox-button';
};

/**
 * Get the tag class
 * @returns The tag class string
 */
export const getEduBoxTagClass = (): string => {
  return 'edubox-tag';
};

/**
 * Set custom EduBox height using CSS variables
 * @param element The HTML element to set the height on
 * @param height The height value (e.g., '40%', '300px')
 */
export const setEduBoxHeight = (element: HTMLElement, height: string): void => {
  // Set the CSS variable for height
  element.style.setProperty('--edubox-height', height);
  
  // If height is 'auto', ensure we have a minimum height for scrolling to work
  if (height === 'auto') {
    element.style.minHeight = '200px';
  }
};

/**
 * EduBox component CSS class names
 */
export const eduBoxClasses = {
  container: 'edubox-container',
  embedded: 'embedded',
  content: 'edubox-content',
  empty: 'edubox-empty',
  loading: 'edubox-loading',
  loadingSpinner: 'edubox-loading-spinner',
  highlight: 'edubox-highlight',
  callout: 'edubox-callout',
  calloutTitle: 'edubox-callout-title',
  button: 'edubox-button',
  tag: 'edubox-tag',
};
